import { Injectable } from '@angular/core';
import OlCircleStyle from 'ol/style/Circle';
import OlFill from 'ol/style/Fill';
import OlStroke from 'ol/style/Stroke';
import OlStyle from 'ol/style/Style';
import OlText from 'ol/style/Text';
import { Fill, RegularShape, Stroke, Style, Icon } from 'ol/style';

@Injectable()
export class OpenLayerStyleService {
  private readonly _componentName = 'OpenLayerStyleService';

  private stroke = new Stroke({ color: 'black', width: 2 });
  private fill = new Fill({ color: 'red' });

  private colorOA_RGBA = 'rgba(234, 79, 0, 1)'

  // https://openlayers.org/en/latest/apidoc/module-ol_style_Style-Style.html

  private basicStyles = {
    circle: new Style({
      image: new OlCircleStyle({
        fill: new OlFill({
          color: this.colorOA_RGBA,
        }),
        stroke: new OlStroke({
          color: '#3399CC',
          width: 1.25,
        }),
        radius: 8,
      }),
      fill: new OlFill({
        color: this.colorOA_RGBA,
      }),
      stroke: new OlStroke({
        color: '#3399CC',
        width: 1.25,
      }),
    }),
    square: new Style({
      image: new RegularShape({
        fill: this.fill,
        stroke: this.stroke,
        points: 4,
        radius: 10,
        angle: Math.PI / 4,
      }),
    }),
    rectangle: new Style({
      image: new RegularShape({
        fill: this.fill,
        stroke: this.stroke,
        radius: 10 / Math.SQRT2,
        radius2: 10,
        points: 4,
        angle: 0,
        scale: [1, 0.5],
      }),
    }),
    triangle: new Style({
      image: new RegularShape({
        fill: this.fill,
        stroke: this.stroke,
        points: 3,
        radius: 10,
        rotation: Math.PI / 4,
        angle: 0,
      }),
    }),
    star: new Style({
      image: new RegularShape({
        fill: this.fill,
        stroke: this.stroke,
        points: 5,
        radius: 10,
        radius2: 4,
        angle: 0,
      }),
    }),
    cross: new Style({
      image: new RegularShape({
        fill: this.fill,
        stroke: this.stroke,
        points: 4,
        radius: 10,
        radius2: 0,
        angle: 0,
      }),
    }),
    x: new Style({
      image: new RegularShape({
        fill: this.fill,
        stroke: this.stroke,
        points: 4,
        radius: 10,
        radius2: 0,
        angle: Math.PI / 4,
      }),
    }),
    stacked: [
      new Style({
        image: new RegularShape({
          fill: this.fill,
          stroke: this.stroke,
          points: 4,
          radius: 5,
          angle: Math.PI / 4,
          displacement: [0, 10],
        }),
      }),
      new Style({
        image: new RegularShape({
          fill: this.fill,
          stroke: this.stroke,
          points: 4,
          radius: 10,
          angle: Math.PI / 4,
        }),
      }),
    ],
  };

  private _trailStyle: Style = new Style({
    stroke: new Stroke({
      color: 'orange',
      lineDash: [4],
      width: 3,
    }),
    fill: new Fill({
      color: 'rgba(0, 0, 255, 0.1)',
    }),
  });

  private _boundaryStyle = new Style({
    stroke: new Stroke({
      color: 'rgba(255, 0, 0, 0.8)',
      lineDash: [4],
      width: 3,
    }),
    fill: new Fill({
      color: 'rgba(255,255, 255, 0)',
    }),
  });

  private styles: Record<string, Style> = {};

  constructor() {
    this.styles['Agreement Parcels'] = new Style({
      stroke: new Stroke({
        color: 'rgba(226, 96, 25, 0.8)',
        lineDash: [4],
        width: 6,
      }),
      fill: new Fill({
        color: 'rgba(255,255, 255, 0)',
      }),
    });

    this.styles['Agreement Parcel Locations'] = this.basicStyles.circle;

    this.styles['Selected Parcel Locations'] = this.basicStyles.circle;

    
  }

  /**
   * Gets a default point with optional label from field
   * @param feature
   * @param labelField
   */
  public GetDefaultPointStyle(feature: any, labelField: string) {
    let s = new OlStyle({
      image: new OlCircleStyle({
        radius: 5,
        fill: new OlFill({ color: 'black' }),
        stroke: new OlStroke({ color: 'black', width: 1 }),
      }),
    });

    let labelValue = feature.get(labelField);

    s.setText(this.GetDefaultTextLabel(labelValue));

    return s;
  }

  /**
   * Gets the style for a trail feature
   */
  public get trailStyle(): Style {
    return this._trailStyle;
  }

  /**
   * Gets the style for a listing boundary feature
   */
  public get boundaryStyle(): Style {
    return this._boundaryStyle;
  }

  /**
   * Creates a basic point style
   * @param key (star)
   * @param strokeColor
   * @param fillColor
   */
  public createBasicPointStyle(key: string, strokeColor: string, fillColor: string) {
    switch (key.toLowerCase()) {
      case 'star':
        return new Style({
          image: new RegularShape({
            fill: new Fill({ color: fillColor }),
            stroke: new Stroke({ color: strokeColor, width: 1 }),
            points: 5,
            radius: 10,
            radius2: 4,
            angle: 0,
          }),
        });

        break;

      default:
        break;
    }
  }

  /**
   * Gets a default OpenLayer Text Object with text set to the text parameter
   * @param text
   */
  public GetDefaultTextLabel(text: string) {
    // https://openlayers.org/en/latest/apidoc/module-ol_style_Text-Text.html
    let ofTheJedi = new OlText({
      text: text,
      scale: 1,
      offsetY: -10,
      fill: new OlFill({
        color: '#000000',
      }),
      stroke: new OlStroke({
        color: '#FFFF99',
        width: 3,
      }),
    });

    return ofTheJedi;
  }

  /**
   * Gets a polygon symbol for displaying geometry associated
   * with generalized areas.  Red outline with semi-transparent blue fill.
   */
  public GetVicinityPolygonSymbol() {
    return new Style({
      stroke: new Stroke({
        color: 'red',
        width: 3,
      }),
      fill: new Fill({
        color: 'rgba(0, 0, 255, 0.1)',
      }),
    });
  }

  /**
   * Creates an icon style using a url to the raster image file (e.g. png)
   * @param iconUrl
   * @param scale - Resizes the icon's width/height
   */
  public createIconStyle(iconUrl: string, scale?: number) {
    if (!scale) {
      scale = 1;
    }

    return new Style({
      image: new Icon({
        anchor: [0.5, 0.5],
        scale: [scale, scale],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        src: iconUrl,
      }),
    });
  }

  public getStyle(key: string) {
    return this.styles[key];
  }

  /**
   * Gets all available styles
   * @returns
   */
  public getAllStyles() {
    return this.styles;
  }
}
